<script setup>
import { computed, onBeforeUnmount, ref } from 'vue'
import {
  Collapse as ACollapse,
  CollapseItem as ACollapseItem,
  Message,
} from '@arco-design/web-vue'
import {
  IconArrowRight,
  IconCheck,
  IconCopy,
  IconDownload,
  IconInfoCircle,
} from '@arco-design/web-vue/es/icon'

const asset = (name) =>
  new URL(`../../static/img/bt/icons/${name}.svg`, import.meta.url).href

const stats = [
  { value: '1000万+', label: '服务器用户' },
  { value: '99.9%', label: '稳定运行' },
  { value: '50%+', label: '运维效率提升' },
]

const whyItems = [
  { icon: 'easy', title: '简单易用', desc: '图形化操作' },
  { icon: 'shield', title: '稳定可靠', desc: '持续更新维护' },
  { icon: 'community', title: '开源免费', desc: '社区生态活跃' },
]

const features = [
  { icon: 'globe', title: '网站管理', desc: '支持多站点管理，一键部署运行环境，快速建站。' },
  { icon: 'database', title: '数据库管理', desc: '支持 MySQL、MariaDB、PostgreSQL 等数据库管理。' },
  { icon: 'cube', title: '软件商店', desc: '提供丰富的应用，支持一键安装 LNMP、LAMP 及常用软件。' },
  { icon: 'folder', title: '文件管理', desc: '在线文件管理，支持上传、下载、编辑、权限设置。' },
  { icon: 'shield', title: '安全防护', desc: '防火墙、端口管理、SSH 管理、安全加固，守护服务器安全。' },
  { icon: 'clock', title: '计划任务', desc: '支持定时任务、备份任务，让运维更自动化。' },
]

const systems = [
  { icon: 'centos', name: 'CentOS', version: '7 / 8 / 9' },
  { icon: 'ubuntu', name: 'Ubuntu', version: '18.04 / 20.04 / 22.04' },
  { icon: 'debian', name: 'Debian', version: '10 / 11 / 12' },
  { icon: 'opensuse', name: 'openSUSE', version: '15.x' },
  { icon: 'windows', name: 'Windows', version: 'Server 2016 / 2019\n2022 / 10 / 11' },
]

const installTabs = [
  {
    key: 'centos',
    label: 'CentOS / RHEL',
    command:
      'yum install -y wget && wget -O install.sh https://download.bt.cn/install/install_6.0.sh && sh install.sh',
  },
  {
    key: 'ubuntu',
    label: 'Ubuntu / Debian',
    command:
      'wget -O install.sh https://download.bt.cn/install/install-ubuntu_6.0.sh && sudo bash install.sh',
  },
  {
    key: 'suse',
    label: 'openSUSE',
    command:
      'wget -O install.sh https://download.bt.cn/install/install-suse.sh && sudo bash install.sh',
  },
  {
    key: 'windows',
    label: 'Windows',
    command:
      'powershell -ExecutionPolicy Bypass -Command "iwr https://download.bt.cn/win/install.ps1 -OutFile install.ps1; .\\install.ps1"',
  },
]

const mountTabs = [
  {
    key: 'bt',
    label: '宝塔存储挂载',
    command:
      'yum install -y curl && curl -sSO https://download.bt.cn/tools/bt-mount.sh && bash bt-mount.sh',
  },
  {
    key: 'disk',
    label: '数据盘挂载',
    command:
      'curl -sSO https://download.bt.cn/tools/disk.sh && bash disk.sh',
  },
  {
    key: 'custom',
    label: '自定义挂载',
    command:
      'mount /dev/sdb1 /www && echo "/dev/sdb1 /www ext4 defaults 0 0" >> /etc/fstab',
  },
]

const faqs = [
  {
    q: '为什么选择宝塔面板？',
    a: '宝塔面板通过可视化界面简化服务器运维操作，支持主流环境一键部署，适合个人开发者、企业运维团队和中小企业使用。',
  },
  {
    q: '安装过程中提示连接失败怎么办？',
    a: '请先确认服务器可以正常访问外网，并检查 DNS、防火墙、安全组、代理配置以及系统时间是否正确。',
  },
  {
    q: '是否支持已有服务器迁移？',
    a: '支持。建议先完成网站、数据库和配置备份，再使用迁移或同步工具进行迁移，完成后逐项验证站点与服务状态。',
  },
  {
    q: '宝塔面板是否免费？',
    a: '基础功能可免费使用；部分增强功能、专业服务或扩展能力可能根据具体版本提供不同授权方案。',
  },
]

const activeInstall = ref('centos')
const activeMount = ref('bt')
const copiedKey = ref('')
const installSection = ref(null)

const currentInstallCommand = computed(
  () => installTabs.find((item) => item.key === activeInstall.value)?.command ?? ''
)

const currentMountCommand = computed(
  () => mountTabs.find((item) => item.key === activeMount.value)?.command ?? ''
)

let copyTimer

async function copyText(text, key) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
    } else {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.readOnly = true
      textarea.style.cssText = 'position:fixed;opacity:0;pointer-events:none'
      document.body.appendChild(textarea)
      textarea.select()

      try {
        if (!document.execCommand('copy')) throw new Error('copy failed')
      } finally {
        textarea.remove()
      }
    }

    copiedKey.value = key
    Message.success('复制成功')

    window.clearTimeout(copyTimer)
    copyTimer = window.setTimeout(() => {
      copiedKey.value = ''
    }, 1600)
  } catch {
    Message.error('复制失败，请手动复制')
  }
}

function scrollToInstall() {
  installSection.value?.scrollIntoView({
    behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth',
    block: 'center',
  })
}

onBeforeUnmount(() => window.clearTimeout(copyTimer))
</script>

<template>
  <main class="bt-page">
    <section class="hero" aria-labelledby="bt-title">
      <img
        class="hero-art"
        src="../../static/img/bt/hero.webp"
        alt="宝塔面板服务器管理界面，包含服务器状态、流量监控和常用管理功能"
        fetchpriority="high"
      />

      <div class="hero-copy">
        <div class="brand-line">
          <img class="brand-logo green-icon" :src="asset('cube')" alt="" />
          <span>宝塔面板</span>
          <span class="brand-slogan">简单　稳定　高效</span>
        </div>

        <h1 id="bt-title">让服务器管理<br />更简单</h1>

        <p class="hero-desc">
          宝塔面板是一款广受欢迎的服务器运维管理软件，<br class="desktop-break" />
          通过可视化界面，帮助你轻松完成网站、数据库、<br class="desktop-break" />
          环境部署与安全管理，专注于让运维变得简单、高效、可靠。
        </p>

        <div class="hero-actions">
          <button type="button" class="primary-btn" @click="scrollToInstall">
            <IconDownload />
            立即安装
          </button>

          <a
            class="ghost-btn"
            href="https://demo.bt.cn/login"
            target="_blank"
            rel="noopener noreferrer"
          >
            查看演示
            <IconArrowRight />
          </a>
        </div>

        <div class="hero-stats">
          <div v-for="item in stats" :key="item.label" class="stat-item">
            <strong>{{ item.value }}</strong>
            <span>{{ item.label }}</span>
          </div>
        </div>
      </div>
    </section>

    <div class="content-shell">
      <section class="intro-grid" aria-label="宝塔面板介绍">
        <article class="why-card">
          <div class="why-heading">
            <span class="product-icon">
              <img class="green-icon" :src="asset('cube')" alt="" />
            </span>

            <div>
              <span class="section-kicker">PRODUCT</span>
              <h2>为什么选择宝塔面板？</h2>
              <p>
                集网站管理、环境部署、安全防护、监控运维于一体，<br class="desktop-break" />
                为个人开发者、企业运维团队和中小企业提供简单可靠的服务器管理解决方案。
              </p>
            </div>
          </div>

          <div class="why-list">
            <div v-for="item in whyItems" :key="item.title" class="why-item">
              <span class="round-icon">
                <img class="green-icon" :src="asset(item.icon)" alt="" />
              </span>
              <div>
                <b>{{ item.title }}</b>
                <small>{{ item.desc }}</small>
              </div>
            </div>
          </div>
        </article>

        <article class="experience-card">
          <div class="experience-copy">
            <span class="section-kicker">FOR A BETTER SERVER</span>
            <h2>更专注的<br />服务器运维体验</h2>
            <span class="short-line" aria-hidden="true"></span>
            <p>把复杂的命令行操作，<br />变成简单的点击操作。</p>
          </div>

          <img
            class="cube-art"
            src="../../static/img/bt/cubes.webp"
            alt=""
            loading="lazy"
          />

          <div class="exp-tags">
            <span v-for="tag in ['简单', '高效', '安全', '可靠']" :key="tag">
              {{ tag }}
              <span class="tag-dot" aria-hidden="true"></span>
            </span>
          </div>
        </article>
      </section>

      <section class="panel feature-panel" aria-labelledby="features-title">
        <div class="feature-heading">
          <div>
            <span class="section-kicker">FEATURES</span>
            <h2 id="features-title">核心能力</h2>
          </div>

          <p>覆盖服务器运维的各类场景，满足从建站到运维的完整需求。</p>
          <span class="panel-note">简单的工具，强大的能力</span>
        </div>

        <div class="feature-grid">
          <article v-for="item in features" :key="item.title" class="feature-card">
            <span class="feature-icon">
              <img class="green-icon" :src="asset(item.icon)" alt="" />
            </span>

            <div class="feature-copy">
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
            </div>

            <span class="feature-arrow" aria-hidden="true">
              <IconArrowRight />
            </span>
          </article>
        </div>
      </section>

      <section class="panel support-panel" aria-labelledby="support-title">
        <div class="section-side-copy">
          <span class="section-kicker">SUPPORT</span>
          <h2 id="support-title">系统支持</h2>
          <p>
            支持主流 Linux 发行版与 Windows 系统，<br class="desktop-break" />
            满足不同场景的服务器管理需求。
          </p>
        </div>

        <div class="system-grid">
          <div v-for="item in systems" :key="item.name" class="system-card">
            <img class="system-logo" :src="asset(item.icon)" alt="" loading="lazy" />
            <b>{{ item.name }}</b>
            <small>{{ item.version }}</small>
          </div>
        </div>
      </section>

      <section
        ref="installSection"
        class="panel command-panel"
        aria-labelledby="install-title"
      >
        <div class="section-side-copy">
          <span class="section-kicker">INSTALL</span>
          <h2 id="install-title">安装命令</h2>
          <p>根据你的服务器系统，复制对应的安装命令，在服务器中执行即可快速安装宝塔面板。</p>
        </div>

        <div class="command-content">
          <div class="switch-tabs" role="group" aria-label="选择安装系统">
            <button
              v-for="item in installTabs"
              :key="item.key"
              type="button"
              :aria-pressed="activeInstall === item.key"
              :class="{ active: activeInstall === item.key }"
              @click="activeInstall = item.key"
            >
              {{ item.label }}
            </button>
          </div>

          <div class="code-box">
            <Transition name="code-switch" mode="out-in">
              <code
                :key="activeInstall"
                tabindex="0"
                aria-label="安装命令"
              >{{ currentInstallCommand }}</code>
            </Transition>

            <button
              type="button"
              class="copy-btn"
              :class="{ copied: copiedKey === `install-${activeInstall}` }"
              aria-label="复制安装命令"
              @click="copyText(currentInstallCommand, `install-${activeInstall}`)"
            >
              <IconCheck v-if="copiedKey === `install-${activeInstall}`" />
              <IconCopy v-else />
              <span aria-live="polite">
                {{ copiedKey === `install-${activeInstall}` ? '已复制' : '复制' }}
              </span>
            </button>
          </div>

          <p class="command-tip">
            <IconInfoCircle />
            安装过程中如遇问题，请确保以 root 用户执行，并检查服务器网络是否正常。
          </p>
        </div>
      </section>

      <section class="panel command-panel mount-panel" aria-labelledby="mount-title">
        <div class="section-side-copy">
          <span class="section-kicker">MOUNT</span>
          <h2 id="mount-title">挂载命令</h2>
          <p>支持宝塔存储挂载、数据盘挂载，轻松管理服务器存储空间。</p>
        </div>

        <div class="command-content">
          <div class="switch-tabs" role="group" aria-label="选择挂载方式">
            <button
              v-for="item in mountTabs"
              :key="item.key"
              type="button"
              :aria-pressed="activeMount === item.key"
              :class="{ active: activeMount === item.key }"
              @click="activeMount = item.key"
            >
              {{ item.label }}
            </button>
          </div>

          <div class="code-box">
            <Transition name="code-switch" mode="out-in">
              <code
                :key="activeMount"
                tabindex="0"
                aria-label="挂载命令"
              >{{ currentMountCommand }}</code>
            </Transition>

            <button
              type="button"
              class="copy-btn"
              :class="{ copied: copiedKey === `mount-${activeMount}` }"
              aria-label="复制挂载命令"
              @click="copyText(currentMountCommand, `mount-${activeMount}`)"
            >
              <IconCheck v-if="copiedKey === `mount-${activeMount}`" />
              <IconCopy v-else />
              <span aria-live="polite">
                {{ copiedKey === `mount-${activeMount}` ? '已复制' : '复制' }}
              </span>
            </button>
          </div>

          <p class="command-tip">
            <IconInfoCircle />
            支持自动挂载、开机自启，详细使用方法请参考官方文档。
          </p>
        </div>
      </section>

      <section class="panel faq-panel" aria-labelledby="faq-title">
        <div class="section-side-copy">
          <span class="section-kicker">FAQ</span>
          <h2 id="faq-title">常见问题</h2>
          <p>整理用户最关心的问题，帮助你快速上手和解决使用中的疑问。</p>
        </div>

        <ACollapse
          class="faq-collapse"
          :default-active-key="['0']"
          accordion
          expand-icon-position="right"
        >
          <ACollapseItem
            v-for="(item, index) in faqs"
            :key="item.q"
            :item-key="String(index)"
          >
            <template #header>
              <span class="faq-title">
                <span class="faq-number">{{ String(index + 1).padStart(2, '0') }}</span>
                <b>{{ item.q }}</b>
              </span>
            </template>
            <p class="faq-answer">{{ item.a }}</p>
          </ACollapseItem>
        </ACollapse>
      </section>
    </div>
  </main>
</template>


<style lang="less" scoped>
@font-face {
  font-family: BTDin;
  src: url('../../static/font/file/DINPro-Medium.woff') format('woff');
  font-weight: 500 800;
  font-display: swap;
}

:global(body) {
  margin: 0;
  background: #f2f9f5;
}

.bt-page {
  --u: min(0.0694444444vw, 1px);
  --green: #168744;
  --deep-green: #064f30;
  --ink: #101d22;
  --muted: #7d8896;

  width: 100%;
  margin-top: var(--head-height, 0);
  color: var(--ink);
  background: #f3faf7;
  font: calc(11.5 * var(--u)) / 1.6 'Microsoft YaHei', 'PingFang SC', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;

  h2 {
    font-size: calc(18 * var(--u));
    line-height: 1.4;
  }

  button,
  a {
    font: inherit;
    -webkit-tap-highlight-color: transparent;
  }

  button {
    cursor: pointer;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  img {
    display: block;
    max-width: 100%;
  }

  button,
  a,
  code {
    &:focus-visible {
      outline: 2px solid #187d49;
      outline-offset: 3px;
    }
  }
}

.green-icon {
  filter: invert(37%) sepia(82%) saturate(503%) hue-rotate(94deg) brightness(92%) contrast(97%);
}

/* Banner */
.hero {
  position: relative;
  width: 100%;
  height: calc(430 * var(--u));
  overflow: hidden;
  animation: fadeIn .6s ease both;
}

.hero-art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  max-width: none !important;
  object-fit: cover;
  object-position: center;
  animation: heroIn 1s cubic-bezier(.2, .7, .2, 1) both;
}

.hero-copy {
  position: relative;
  z-index: 1;
  width: calc(100% - 48px);
  max-width: 80rem;
  margin: 0 auto;
  padding-top: calc(34 * var(--u));

  > * {
    max-width: calc(340 * var(--u));
  }
}

.brand-line {
  display: flex;
  align-items: center;
  gap: calc(10 * var(--u));
  height: calc(22 * var(--u));
  color: #6a9385;
  font-size: calc(11.5 * var(--u));
  white-space: nowrap;
}

.brand-logo {
  width: calc(20 * var(--u));
  height: calc(22 * var(--u));
}

.brand-slogan {
  margin-left: calc(8 * var(--u));
}

.hero h1 {
  margin-top: calc(22 * var(--u));
  color: #071b1c;
  font-size: calc(40 * var(--u));
  font-weight: 900;
  line-height: 1.2;
  letter-spacing: -.025em;
}

.hero-desc {
  margin-top: calc(13 * var(--u)) !important;
  color: #73818b;
  font-size: calc(11.5 * var(--u));
  line-height: 1.7;
}

.hero-actions {
  display: flex;
  gap: calc(12 * var(--u));
  margin-top: calc(20 * var(--u));
}

.primary-btn,
.ghost-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: calc(8 * var(--u));
  height: calc(40 * var(--u));
  border: 1px solid #8ab6a1;
  border-radius: calc(5 * var(--u));
  font-size: calc(12.5 * var(--u)) !important;
  font-weight: 700 !important;
  white-space: nowrap;
  transition: .22s ease;
}

.primary-btn {
  width: calc(126 * var(--u));
  color: #fff;
  background: #257d50;
  border-color: #247c50;

  &:hover {
    background: #176940;
    transform: translateY(calc(-2 * var(--u)));
  }

  .arco-icon {
    font-size: calc(16 * var(--u));
  }
}

.ghost-btn {
  width: calc(108 * var(--u));
  color: var(--deep-green) !important;
  background: #ffffff45;

  &:hover {
    background: #fff;
    border-color: #268452;
    transform: translateY(calc(-2 * var(--u)));
  }
}

.hero-stats {
  display: flex;
  margin-top: calc(28 * var(--u));
}

.stat-item {
  display: flex;
  flex: 0 0 calc(96 * var(--u));
  flex-direction: column;
  min-width: 0;
  padding-left: calc(20 * var(--u));
  border-left: 1px solid #d7eae2;
  line-height: 1.2;

  &:first-child {
    flex-basis: calc(91 * var(--u));
    padding-left: 0;
    border-left: 0;
  }

  strong {
    color: var(--deep-green);
    font: 700 calc(20 * var(--u)) / 1 BTDin, 'Microsoft YaHei', sans-serif;
    white-space: nowrap;
  }

  span {
    margin-top: calc(6 * var(--u));
    color: #7e8c97;
    white-space: nowrap;
  }
}

/* Content */
.content-shell {
  display: grid;
  gap: calc(12 * var(--u));
  width: calc(100% - 40px);
  max-width: 80rem;
  margin: calc(18 * var(--u)) auto 0;
  padding-bottom: calc(24 * var(--u));
}

.intro-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: calc(10 * var(--u));
}

.why-card,
.panel {
  background: #ffffffd6;
  border: 1px solid #fff;
  border-radius: calc(14 * var(--u));
}

.why-card {
  padding: calc(18 * var(--u)) calc(17 * var(--u)) calc(16 * var(--u));
}

.why-heading {
  display: flex;
  align-items: flex-start;
  gap: calc(15 * var(--u));

  h2 {
    margin-top: calc(1 * var(--u));
    font-size: calc(18 * var(--u));
  }

  p {
    margin-top: calc(9 * var(--u));
    color: var(--muted);
    font-size: calc(11 * var(--u));
    line-height: 1.7;
  }
}

.product-icon {
  display: grid;
  flex: 0 0 calc(46 * var(--u));
  width: calc(46 * var(--u));
  height: calc(46 * var(--u));
  place-items: center;
  border-radius: calc(11 * var(--u));
  background: #e8f6ee;

  img {
    width: calc(28 * var(--u));
  }
}

.section-kicker {
  display: block;
  color: #8abcaa;
  font-size: calc(11 * var(--u));
}

.why-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: calc(10 * var(--u));
  margin-top: calc(19 * var(--u));
}

.why-item {
  display: flex;
  align-items: center;
  gap: calc(8 * var(--u));
  min-height: calc(52 * var(--u));
  padding: calc(8 * var(--u));
  border: 1px solid #edf3f0;
  border-radius: calc(7 * var(--u));
  background: #ffffffa6;

  b,
  small {
    display: block;
    white-space: nowrap;
  }

  b {
    font-size: calc(11.5 * var(--u));
  }

  small {
    color: var(--muted);
    font-size: calc(10 * var(--u));
  }
}

.round-icon,
.feature-icon {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  border-radius: 50%;
  background: #e5f4ec;
}

.round-icon {
  width: calc(35 * var(--u));
  height: calc(35 * var(--u));

  img {
    width: calc(21 * var(--u));
  }
}

.experience-card {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  padding: calc(24 * var(--u)) calc(25 * var(--u));
  background: #e4f3eb;
  border: calc(6 * var(--u)) solid #ffffffdb;
  border-radius: calc(14 * var(--u));
}

.experience-copy {
  z-index: 1;

  .section-kicker {
    font-size: calc(10 * var(--u));
  }

  h2 {
    margin-top: calc(10 * var(--u));
    color: #0e3028;
    font-size: calc(20 * var(--u));
    line-height: 1.35;
  }

  p {
    color: #47625b;
    font-size: calc(12 * var(--u));
    line-height: 1.8;
  }
}

.short-line {
  display: block;
  width: calc(21 * var(--u));
  height: 1px;
  margin: calc(20 * var(--u)) 0;
  background: #438263;
}

.cube-art {
  position: absolute;
  top: calc(32 * var(--u));
  right: calc(83 * var(--u));
  width: calc(188 * var(--u));
  height: calc(188 * var(--u));
  object-fit: contain;
  mix-blend-mode: multiply;
}

.exp-tags {
  position: absolute;
  top: calc(42 * var(--u));
  right: calc(21 * var(--u));
  display: grid;
  gap: calc(10 * var(--u));

  > span {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: calc(10 * var(--u));
    width: calc(62 * var(--u));
    height: calc(29 * var(--u));
    color: #42735a;
    background: #f7fcf9db;
    border-radius: calc(7 * var(--u));
    font-size: calc(11 * var(--u));
    font-weight: 700;
  }
}

.tag-dot {
  width: calc(8 * var(--u));
  height: calc(8 * var(--u));
  border: calc(3 * var(--u)) solid #218945;
  border-radius: 50%;
  background: #fff;
}

.panel {
  padding: calc(14 * var(--u)) calc(19 * var(--u));
}

.feature-heading {
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: calc(18 * var(--u));
  margin-bottom: calc(13 * var(--u));

  > p {
    margin-bottom: calc(3 * var(--u));
    color: var(--muted);
    font-size: calc(11.5 * var(--u));
  }
}

.panel-note {
  position: absolute;
  top: calc(6 * var(--u));
  right: 0;
  color: var(--muted);
  font-size: calc(11 * var(--u));
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: calc(10 * var(--u));
}

.feature-card {
  position: relative;
  display: flex;
  gap: calc(16 * var(--u));
  min-height: calc(86 * var(--u));
  padding: calc(14 * var(--u)) calc(15 * var(--u));
  background: #f9fcfb;
  border: 1px solid #e3eeea;
  border-radius: calc(6 * var(--u));
}

.feature-icon {
  width: calc(49 * var(--u));
  height: calc(49 * var(--u));

  img {
    width: calc(29 * var(--u));
  }
}

.feature-copy {
  min-width: 0;
  padding-top: calc(3 * var(--u));

  h3 {
    font-size: calc(12.5 * var(--u));
  }

  p {
    margin-top: calc(5 * var(--u));
    padding-right: calc(9 * var(--u));
    color: var(--muted);
    font-size: calc(10.8 * var(--u));
    line-height: 1.6;
  }
}

.feature-arrow {
  position: absolute;
  right: calc(12 * var(--u));
  bottom: calc(10 * var(--u));
  display: grid;
  width: calc(23 * var(--u));
  height: calc(23 * var(--u));
  place-items: center;
  color: #244c45;
  background: #e3ece9;
  border-radius: 50%;
  transition: transform .22s ease;
}

.support-panel,
.command-panel,
.faq-panel {
  display: grid;
  align-items: center;
  gap: calc(16 * var(--u));
}

.support-panel {
  grid-template-columns: calc(244 * var(--u)) 1fr;
  padding: calc(7 * var(--u)) calc(7 * var(--u)) calc(7 * var(--u)) calc(19 * var(--u));
}

.command-panel,
.faq-panel {
  grid-template-columns: calc(178 * var(--u)) minmax(0, 1fr);
  padding: calc(9 * var(--u)) calc(6 * var(--u)) calc(7 * var(--u)) calc(19 * var(--u));
}

.section-side-copy p {
  margin-top: calc(8 * var(--u));
  color: var(--muted);
  font-size: calc(12 * var(--u));
  line-height: 1.5;
}

.system-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: calc(13 * var(--u));
}

.system-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(92 * var(--u));
  border: 1px solid #e6efec;
  border-radius: calc(6 * var(--u));
  background: #ffffffc2;

  b {
    margin-top: calc(7 * var(--u));
    font: 500 calc(12 * var(--u)) / 1.35 BTDin, sans-serif;
  }

  small {
    margin-top: calc(3 * var(--u));
    color: var(--muted);
    font-size: calc(10 * var(--u));
    white-space: pre-line;
    text-align: center;
  }
}

.system-logo {
  width: calc(35 * var(--u));
  height: calc(35 * var(--u));
  object-fit: contain;
}

/* Command */
.command-content {
  min-width: 0;
  padding: calc(6 * var(--u)) calc(13 * var(--u)) calc(8 * var(--u));
  background: #ffffff70;
  border: 1px solid #eaf2ee;
  border-radius: calc(9 * var(--u));
}

.switch-tabs {
  display: flex;
  gap: calc(4 * var(--u));
  margin-bottom: calc(7 * var(--u));

  button {
    position: relative;
    min-width: calc(81 * var(--u));
    height: calc(32 * var(--u));
    padding: 0 calc(19 * var(--u));
    color: #74818c;
    background: #f2f5f6;
    border: 1px solid transparent;
    border-radius: calc(5 * var(--u));
    font-size: calc(10.5 * var(--u));
    transition: color .22s ease, background .22s ease, transform .22s ease;

    &::after {
      position: absolute;
      bottom: 0;
      left: 50%;
      width: 0;
      height: calc(2 * var(--u));
      background: var(--green);
      content: '';
      transform: translateX(-50%);
      transition: width .24s ease;
    }

    &:hover {
      color: #195738;
      background: #eaf3ee;
    }

    &:active {
      transform: scale(.98);
    }

    &.active {
      color: #18583d;
      background: #e3f2eb;

      &::after {
        width: calc(24 * var(--u));
      }
    }
  }
}

.code-box {
  display: flex;
  align-items: center;
  gap: calc(10 * var(--u));
  height: calc(39 * var(--u));
  padding: calc(5 * var(--u)) calc(14 * var(--u)) calc(5 * var(--u)) calc(16 * var(--u));
  color: #d5e1e4;
  background: #2c383e;
  border: 1px solid #1e2f35;
  border-radius: calc(5 * var(--u));

  code {
    display: block;
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    padding: calc(5 * var(--u)) 0;
    font: calc(11.3 * var(--u)) / 1.5 Consolas, Monaco, monospace;
    white-space: nowrap;
    scrollbar-width: thin;
  }
}

.copy-btn {
  display: flex;
  flex: 0 0 calc(57 * var(--u));
  align-items: center;
  justify-content: center;
  gap: calc(5 * var(--u));
  width: calc(57 * var(--u));
  height: calc(26 * var(--u));
  padding: 0;
  color: #f4faf7;
  background: #ffffff20;
  border: 0;
  border-radius: calc(4 * var(--u));
  font-size: calc(10 * var(--u)) !important;
  transition: .2s ease;

  &:hover {
    background: #ffffff35;
  }

  &:active {
    transform: scale(.96);
  }

  &.copied {
    color: #c2f0d5;
    background: #2c704b;
  }
}

.command-tip {
  display: flex;
  align-items: flex-start;
  gap: calc(8 * var(--u));
  margin-top: calc(7 * var(--u)) !important;
  color: var(--muted);
  font-size: calc(10 * var(--u));
  line-height: 1.5;

  .arco-icon {
    flex-shrink: 0;
    margin-top: calc(1 * var(--u));
    color: #819b9b;
    font-size: calc(13 * var(--u));
  }
}

/* Arco FAQ */
.faq-collapse {
  min-width: 0;
  overflow: hidden;
  background: transparent;
  border: 0;
  

  :deep(.arco-collapse-item) {
    margin-bottom: calc(5 * var(--u));
    overflow: hidden;
    background: #f2f6f6;
    border: 0;
    border-radius: calc(6 * var(--u));
    transition: background-color .22s ease;
  }

  :deep(.arco-collapse-item:last-child) {
    margin-bottom: 0;
  }

  :deep(.arco-collapse-item-active) {
    background: #fff;
  }

  :deep(.arco-collapse-item-header) {
    min-height: calc(36 * var(--u));
    padding: calc(5 * var(--u)) calc(10 * var(--u));
    color: #28333c;
    background: transparent;
    border: 0;
    font-size: inherit;
    transition: background-color .2s ease;
  }

  :deep(.arco-collapse-item-header:hover) {
    background: #e8f3ed;
  }

  :deep(.arco-collapse-item-content) {
    padding: 0;
    color: var(--muted);
    background: transparent;
    border: 0;
  }

  :deep(.arco-collapse-item-content-box) {
    padding: 0;
  }

  :deep(.arco-collapse-item-icon) {
    color: #5a7369;
  }
}

.faq-title {
  display: flex;
  align-items: center;
  gap: calc(18 * var(--u));
  min-width: 0;

  b {
    font-size: calc(11.5 * var(--u));
  }
}

.faq-number {
  display: grid;
  flex: 0 0 calc(24 * var(--u));
  width: calc(24 * var(--u));
  height: calc(24 * var(--u));
  place-items: center;
  color: #273b3f;
  background: #dfe7e7;
  border-radius: 50%;
  font: 500 calc(14 * var(--u)) / 1 BTDin, sans-serif;
}

.faq-answer {
  padding: calc(5 * var(--u)) calc(20 * var(--u)) calc(10 * var(--u)) calc(52 * var(--u));
  color: var(--muted);
  font-size: calc(10 * var(--u));
  line-height: 1.7;
}

/* Motion */
.intro-grid,
.feature-panel,
.support-panel,
.command-panel,
.faq-panel {
  animation: sectionIn .55s cubic-bezier(.2, .7, .2, 1) both;
}

.feature-panel { animation-delay: .06s; }
.support-panel { animation-delay: .1s; }
.command-panel { animation-delay: .14s; }
.mount-panel { animation-delay: .18s; }
.faq-panel { animation-delay: .22s; }

.why-card,
.experience-card,
.feature-card,
.system-card {
  transition: transform .22s ease, box-shadow .22s ease;
}

.feature-card:hover,
.system-card:hover,
.why-card:hover,
.experience-card:hover {
  transform: translateY(calc(-3 * var(--u)));
  box-shadow: 0 calc(10 * var(--u)) calc(28 * var(--u)) rgba(29, 86, 58, .07);
}

.feature-card:hover .feature-arrow {
  transform: translateX(calc(3 * var(--u)));
}

.code-switch-enter-active,
.code-switch-leave-active {
  transition: opacity .16s ease, transform .2s ease;
}

.code-switch-enter-from {
  opacity: 0;
  transform: translateY(calc(4 * var(--u)));
}

.code-switch-leave-to {
  opacity: 0;
  transform: translateY(calc(-3 * var(--u)));
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes heroIn {
  from { opacity: .8; transform: scale(1.02); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes sectionIn {
  from { opacity: 0; transform: translateY(calc(10 * var(--u))); }
  to { opacity: 1; transform: translateY(0); }
}

/* Mobile */
@media (max-width: 640px) {
  .bt-page {
    --u: 1px;
    font-size: 13px;
    margin-top: 15vw;

    h2 {
      font-size: 22px;
    }
  }

  .hero {
    height: auto;
    min-height: auto;
    padding-bottom: 20px;
    background: #f8fcfa;
  }

  .hero-copy {
    width: auto;
    margin: 0 24px;
    padding-top: 28px;

    > * {
      max-width: 390px;
    }
  }

  .brand-line {
    font-size: 12px;
  }

  .hero h1 {
    margin-top: 24px;
    font-size: clamp(36px, 8.8vw, 48px);
  }

  .hero-desc {
    font-size: 13px;
    line-height: 1.9;
  }

  .desktop-break {
    display: none;
  }

  .hero-actions {
    gap: 10px;
    margin-top: 22px;
  }

  .primary-btn,
  .ghost-btn {
    height: 44px;
  }

  .hero-stats {
    margin-top: 26px;
  }

  .stat-item {
    padding-left: 14px;
    font-size: 11px;

    strong {
      font-size: 22px;
    }
  }

  .content-shell {
    width: auto;
    margin: 12px;
    gap: 12px;
  }

  .intro-grid,
  .support-panel,
  .command-panel,
  .faq-panel {
    grid-template-columns: 1fr;
  }

  .intro-grid {
    gap: 12px;
  }

  .why-card,
  .panel {
    padding: 18px 16px;
  }

  .why-heading h2 {
    font-size: 19px;
  }

  .why-list {
    gap: 6px;
  }

  .why-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 9px;
  }

  .experience-card {
    min-height: 220px;
    padding: 22px 20px;
  }

  .cube-art {
    top: 50px;
    right: 38px;
    width: 156px;
    height: 156px;
    opacity: .72;
  }

  .exp-tags {
    top: 32px;
    right: 10px;

    > span {
      width: 49px;
      gap: 7px;
    }
  }

  .feature-heading {
    display: block;

    > p {
      margin-top: 8px;
      font-size: 12px;
    }
  }

  .panel-note {
    position: static;
    display: block;
    margin-top: 6px;
  }

  .feature-grid {
    grid-template-columns: 1fr;
  }

  .feature-card {
    min-height: 96px;
    padding: 16px;
  }

  .feature-copy {
    h3 { font-size: 15px; }
    p { font-size: 12px; }
  }

  .system-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .system-card {
    min-height: 108px;
  }

  .switch-tabs {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;

    button {
      min-width: 0;
      min-height: 40px;
      padding: 0 8px;
      font-size: 11px;
    }
  }

  .code-box {
    height: 47px;
    padding: 5px 8px;

    code {
      font-size: 11px;
    }
  }

  .copy-btn {
    flex-basis: 62px;
    width: 62px;
    height: 34px;
    font-size: 11px !important;
  }

  .command-tip {
    font-size: 11px;
    line-height: 1.7;
  }

  .faq-collapse {
    :deep(.arco-collapse-item-header) {
      min-height: 46px;
      padding: 6px;
    }
    
    
  }

  .faq-title {
    gap: 10px;

    b {
      font-size: 12px;
    }
  }

  .faq-answer {
    padding: 2px 14px 12px 40px;
    font-size: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .bt-page *,
  .bt-page *::before,
  .bt-page *::after {
    animation: none !important;
    scroll-behavior: auto !important;
    transition: none !important;
  }
}

@media (forced-colors: active) {
  .green-icon {
    filter: none;
  }

  .bt-page button,
  .bt-page a {
    border: 1px solid ButtonText;
  }
}
</style>
